import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  Grid,
  Button,
  Divider,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppLayout from "../../components/AppLayout";
import { generateBillPDF } from "../../utils/pdfGenerator";
import API_BASE_URL from "../../config/api";

export default function BillDetails() {
  const [searchParams] = useSearchParams();
  const billId = searchParams.get("id");
  const navigate = useNavigate();
  const [billData, setBillData] = useState(null);
  const [loading, setLoading] = useState(true);
  const userRole = localStorage.getItem("role");

  useEffect(() => {
    if (billId) {
      fetchBillDetails();
    } else {
      toast.error("No bill ID provided");
      navigate("/bills");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billId]);

  const fetchBillDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/bills/${billId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error("Failed to fetch bill details");
      }

      const data = await response.json();
      setBillData(data.bill);
    } catch (error) {
      console.error("Error fetching bill details:", error);
      toast.error("Failed to load bill details");
      navigate("/bills");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDownloadPDF = () => {
    try {
      generateBillPDF(billData);
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center", 
          minHeight: "60vh",
          background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)"
        }}>
          <CircularProgress sx={{ color: "#c59a58" }} />
        </Box>
      </AppLayout>
    );
  }

  if (!billData) {
    return (
      <AppLayout>
        <Box sx={{ 
          maxWidth: "900px", 
          mx: "auto", 
          mt: 4,
          background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
          p: 3,
          borderRadius: 2
        }}>
          <Typography variant="h5" sx={{ color: "#fff" }}>Bill not found</Typography>
          <Button 
            onClick={() => navigate("/bills")} 
            sx={{ 
              mt: 2,
              color: "#c59a58",
              borderColor: "#c59a58",
              "&:hover": { bgcolor: "rgba(197, 154, 88, 0.1)" }
            }}
            variant="outlined"
          >
            Back to Bills
          </Button>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ 
        p: { xs: 1, sm: 2, md: 4 }, 
        minHeight: "calc(100vh - 80px)",
        // background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)"
      }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/bills")}
          sx={{
            color: "#c59a58",
            mb: 3,
            "&:hover": { bgcolor: "rgba(197, 154, 88, 0.1)" },
          }}
        >
          Back to Bills
        </Button>

        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 500,
              letterSpacing: { xs: 1, sm: 2, md: 3 },
              fontSize: { xs: "1rem", sm: "1.5rem", md: "1.5rem" },
              color: "#c59a58",
              textDecoration: "underline",
              textUnderlineOffset: { xs: 6, sm: 12 },
              textDecorationThickness: 2,
              wordBreak: "break-word",
            }}
          >
            BILL #{billData.BillNumber}
          </Typography>
        </Box>

        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          {/* Customer & Bill Info */}
          <Paper elevation={8} sx={{ 
            mb: 2, 
            p: { xs: 1.5, sm: 2, md: 3 }, 
            borderRadius: 2,
            background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
            border: "1px solid #444"
          }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#c59a58",
                mb: 2,
                fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                letterSpacing: 0.5,
              }}
            >
              Customer Information
            </Typography>
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography sx={{ color: "#888", fontWeight: 600, mb: 0.5, fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.85rem" } }}>
                  Token Number
                </Typography>
                <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1.1rem" } }}>
                  {billData.CustomerId || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ color: "#888", fontWeight: 600, mb: 0.5, fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.85rem" } }}>
                  Customer Name
                </Typography>
                <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1.1rem" } }}>
                  {billData.CustomerName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ color: "#888", fontWeight: 600, mb: 0.5, fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.85rem" } }}>
                  Contact Number
                </Typography>
                <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1.1rem" } }}>
                  {billData.PhoneNumber || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ color: "#888", fontWeight: 600, mb: 0.5, fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.85rem" } }}>
                  Billed By
                </Typography>
                <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1.1rem" } }}>
                  {billData.BilledByName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ color: "#888", fontWeight: 600, mb: 0.5, fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.85rem" } }}>
                  Date & Time
                </Typography>
                <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1.1rem" } }}>
                  {formatDate(billData.CreatedAt)}
                </Typography>
              </Grid>

              {billData.DueDate && (
                <Grid item xs={12} md={6}>
                  <Typography sx={{ color: "#888", fontWeight: 600, mb: 0.5, fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.85rem" } }}>
                    Due Date
                  </Typography>
                  <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1.1rem" } }}>
                    {new Date(billData.DueDate).toLocaleDateString("en-IN", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </Typography>
                </Grid>
              )}
              {billData.OrderDescription && (
                <Grid item xs={12}>
                  <Typography sx={{ color: "#888", fontWeight: 600, mb: 0.5, fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.85rem" } }}>
                    Order Description
                  </Typography>
                  <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1.1rem" }, whiteSpace: 'pre-wrap' }}>
                    {billData.OrderDescription}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Items Table */}
          <Paper elevation={8} sx={{ 
            mb: 2, 
            borderRadius: 2, 
            overflow: "auto",
            background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
            border: "1px solid #444"
          }}>
            <TableContainer sx={{ maxWidth: { xs: "100%", sm: "100%", md: "100%" } }}>
              <Table sx={{ minWidth: { xs: "100%", sm: 600, md: 700 } }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#C4A574" }}>
                    <TableCell sx={{ fontWeight: 700, color: "#000", fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.9rem" }, p: { xs: 0.75, sm: 1, md: 1.5 }, whiteSpace: "nowrap" }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#000", fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.9rem" }, p: { xs: 0.75, sm: 1, md: 1.5 }, whiteSpace: "nowrap" }}>Size</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#000", fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.9rem" }, p: { xs: 0.75, sm: 1, md: 1.5 }, whiteSpace: "nowrap" }} align="right">
                      Qty
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#000", fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.9rem" }, p: { xs: 0.75, sm: 1, md: 1.5 }, whiteSpace: "nowrap" }} align="right">
                      Price
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#000", fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.9rem" }, p: { xs: 0.75, sm: 1, md: 1.5 }, whiteSpace: "nowrap" }} align="right">
                      D
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#000", fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.9rem" }, p: { xs: 0.75, sm: 1, md: 1.5 }, whiteSpace: "nowrap" }} align="right">
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {billData.items && billData.items.length > 0 ? (
                    billData.items.map((item, index) => (
                      <React.Fragment key={item.BillItemId}>
                        <TableRow
                          sx={{
                            "&:hover": { backgroundColor: "rgba(197, 154, 88, 0.1)" },
                            backgroundColor: index % 2 === 0 ? "#2a2a2a" : "#333"
                          }}
                        >
                          <TableCell sx={{ p: { xs: 0.6, sm: 0.8, md: 1.5 }, whiteSpace: "nowrap" }}>
                            <Typography sx={{ fontWeight: 600, color: "#fff", fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.9rem" } }}>
                              {(item.ProductName || item.ProductCode).substring(0, 15)}
                            </Typography>
                            <Typography sx={{ fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" }, color: "#888" }}>
                              {item.ProductCode}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ p: { xs: 0.6, sm: 0.8, md: 1.5 }, color: "#fff", fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.9rem" }, whiteSpace: "nowrap" }}>{item.Size || "N/A"}</TableCell>
                          <TableCell align="right" sx={{ p: { xs: 0.6, sm: 0.8, md: 1.5 }, color: "#fff", fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.9rem" }, whiteSpace: "nowrap" }}>
                            {item.Quantity}
                          </TableCell>
                          <TableCell align="right" sx={{ p: { xs: 0.6, sm: 0.8, md: 1.5 }, color: "#fff", fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.9rem" }, whiteSpace: "nowrap" }}>
                            ₹{parseFloat(item.UnitPrice).toFixed(2)}
                          </TableCell>
                          <TableCell align="right" sx={{ p: { xs: 0.6, sm: 0.8, md: 1.5 }, color: "#fff", fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.9rem" }, whiteSpace: "nowrap" }}>
                            {item.Discount && parseFloat(item.Discount) > 0 
                              ? `₹${parseFloat(item.Discount).toFixed(2)}` 
                              : '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ p: { xs: 0.6, sm: 0.8, md: 1.5 }, fontWeight: 600, color: "#c59a58", fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.9rem" }, whiteSpace: "nowrap" }}>
                            ₹{parseFloat(item.LineTotal).toFixed(2)}
                          </TableCell>
                        </TableRow>
                        {/* Customizations for this item */}
                        {item.customizations && item.customizations.filter(custom => 
                          // Filter out default stitching entries (zero cost and generic description)
                          !(parseFloat(custom.Cost) === 0 && custom.Description.startsWith('Stitching for'))
                        ).length > 0 && (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ bgcolor: "#BC965E", py: 1 }}>
                              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                Customizations:
                              </Typography>
                              {item.customizations
                                .filter(custom => 
                                  // Filter out default stitching entries
                                  !(parseFloat(custom.Cost) === 0 && custom.Description.startsWith('Stitching for'))
                                )
                                .map((custom, idx) => (
                                  <Typography key={idx} variant="body2" sx={{ ml: 2 }}>
                                    • {custom.Type}: {custom.Description} - ₹{parseFloat(custom.Cost).toFixed(2)}
                                  </Typography>
                                ))}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#888" }}>
                        No items found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Bill Summary */}
          <Paper elevation={8} sx={{ 
            mb: 2, 
            p: { xs: 1.5, sm: 2, md: 3 }, 
            borderRadius: 2,
            background: "rgba(0, 0, 0, 0.4)",
            border: "2px solid rgba(196, 165, 116, 0.3)"
          }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#C4A574",
                mb: 3,
                fontSize: "1.1rem",
                letterSpacing: 0.5,
                textAlign: "center",
              }}
            >
              Bill Summary
            </Typography>
            
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "#fff" }}>
              <Typography>SubTotal:</Typography>
              <Typography fontWeight={600}>₹ {parseFloat(billData.SubTotal).toFixed(2)}</Typography>
            </Box>
            
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "#fff" }}>
              <Typography>D:</Typography>
              <Typography fontWeight={600}>- ₹ {parseFloat(billData.Discount || 0).toFixed(2)}</Typography>
            </Box>
            
            {billData.CouponDiscount && parseFloat(billData.CouponDiscount) > 0 && (
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "#ff9800" }}>
                <Typography sx={{ fontWeight: 600 }}>Coupon Applied:</Typography>
                <Typography sx={{ fontWeight: 600 }}>- ₹ {parseFloat(billData.CouponDiscount).toFixed(2)}</Typography>
              </Box>
            )}
            
            <Divider sx={{ my: 2, borderColor: "rgba(196, 165, 116, 0.2)" }} />
            
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "#fff" }}>
              <Typography variant="h6">Total Amount:</Typography>
              <Typography variant="h6" fontWeight={700}>₹ {parseFloat(billData.TotalAmount).toFixed(2)}</Typography>
            </Box>
            
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "#fff" }}>
              <Typography>Advance Paid:</Typography>
              <Typography>- ₹ {parseFloat(billData.Advance || 0).toFixed(2)}</Typography>
            </Box>
            
            {billData.SchemeCreditApplied && parseFloat(billData.SchemeCreditApplied) > 0 && (
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, color: "#9c27b0" }}>
                <Typography sx={{ fontWeight: 600 }}>Scheme Credit Applied:</Typography>
                <Typography sx={{ fontWeight: 600 }}>- ₹ {parseFloat(billData.SchemeCreditApplied).toFixed(2)}</Typography>
              </Box>
            )}
            
            <Divider sx={{ my: 2, borderColor: "rgba(196, 165, 116, 0.2)" }} />
            
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                color: "#C4A574",
                fontWeight: 700,
                fontSize: "1.1rem",
              }}
            >
              <Typography>Balance Due:</Typography>
              <Typography>₹ {parseFloat(billData.Balance).toFixed(2)}</Typography>
            </Box>
          </Paper>

          {/* Coupon Information */}
          <Paper elevation={8} sx={{ 
            mb: 2, 
            p: { xs: 1.5, sm: 2, md: 3 }, 
            borderRadius: 2,
            background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
            border: "1px solid #444"
          }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#c59a58",
                mb: 2,
                fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                letterSpacing: 0.5,
              }}
            >
              Coupon Information
            </Typography>
            <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
              <Grid item xs={12}>
                <Typography sx={{ color: "#888", fontWeight: 600, mb: 0.5, fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.85rem" } }}>
                  New Coupon Generated
                </Typography>
                <Typography 
                  sx={{ 
                    fontFamily: 'monospace',
                    color: '#ff9800',
                    fontWeight: 600,
                    fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1.1rem" }
                  }}
                >
                  {billData.CouponCode ? (
                    <>
                      {billData.CouponCode}
                      <Typography component="span" sx={{ fontSize: "0.8rem", ml: 1, color: "#888" }}>
                        (₹{billData.CouponAmount ? parseFloat(billData.CouponAmount).toFixed(2) : '0.00'})
                      </Typography>
                    </>
                  ) : "N/A"}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Customization Feedback */}
          {billData.WasCustomizationGood && billData.WasCustomizationGood !== "N/A" && (
            <Paper elevation={8} sx={{ 
              mb: 2, 
              p: { xs: 1.5, sm: 2, md: 3 }, 
              borderRadius: 2,
              background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
              border: "1px solid #444"
            }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#c59a58",
                  mb: 1,
                  fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                  letterSpacing: 0.5,
                }}
              >
                Customization Feedback
              </Typography>
              <Typography sx={{ color: "#fff", fontSize: { xs: "0.8rem", sm: "0.9rem", md: "0.95rem" } }}>
                Was customization good? <strong style={{ color: "#c59a58" }}>{billData.WasCustomizationGood}</strong>
              </Typography>
            </Paper>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap", mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                // Navigate back to bills list (page will be restored from sessionStorage)
                navigate("/bills");
              }}
              sx={{
                borderColor: "#c59a58",
                color: "#c59a58",
                fontWeight: 700,
                fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                px: { xs: 2, sm: 3, md: 4 },
                py: { xs: 0.8, sm: 1, md: 1.2 },
                "&:hover": {
                  bgcolor: "rgba(197, 154, 88, 0.1)",
                  borderColor: "#c59a58",
                },
              }}
            >
              Back to Bills
            </Button>
            {userRole === "Admin" && (
              <>
                <Button
                  variant="contained"
                  onClick={handleDownloadPDF}
                  startIcon={<DownloadIcon />}
                  sx={{
                    bgcolor: "#c59a58",
                    color: "#000",
                    fontWeight: 700,
                    fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                    px: { xs: 2, sm: 3, md: 4 },
                    py: { xs: 0.8, sm: 1, md: 1.2 },
                    "& .MuiButton-startIcon": { mr: { xs: 0.25, sm: 0.5, md: 1 } },
                    "&:hover": { bgcolor: "#b68649" },
                  }}
                >
                  Download Bill
                </Button>
              </>
            )}
          </Box>

          {/* Decorative Fade */}
          <Box
            sx={{
              mt: 6,
              height: 60,
              background: "linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.3))",
              borderRadius: 2,
            }}
          />
        </Box>

      </Box>
    </AppLayout>
  );
}
